import { addWorkingDays, endOfTask, previousWorkingDay, startOfTask, workingDaysBetween, } from './calendar';
import { minDate, requireIsoDate } from './date';
import { forwardPass, } from './forward-pass';
import { err, ok, } from './types';
/** Latest finish a single relationship permits for its predecessor. */
function latestFinishFrom(calendar, dependency, successor, predecessorDuration) {
    const lag = dependency.lagDays;
    switch (dependency.type) {
        case 'FS':
            return addWorkingDays(calendar, successor.lateStart, -(1 + lag));
        case 'FF':
            return addWorkingDays(calendar, successor.lateFinish, -lag);
        case 'SS': {
            const start = addWorkingDays(calendar, successor.lateStart, -lag);
            return endOfTask(calendar, start, predecessorDuration);
        }
        case 'SF': {
            const start = addWorkingDays(calendar, successor.lateFinish, -lag);
            return endOfTask(calendar, start, predecessorDuration);
        }
    }
}
/**
 * Working days this predecessor may slip before its successor would have to
 * move away from its own earliest date. Uses the successor's EARLY dates, which
 * is what distinguishes free float from total float.
 */
function slackAgainst(calendar, dependency, predecessor, successor) {
    const lag = dependency.lagDays;
    switch (dependency.type) {
        case 'FS':
            return workingDaysBetween(calendar, addWorkingDays(calendar, predecessor.earlyFinish, 1 + lag), successor.earlyStart);
        case 'SS':
            return workingDaysBetween(calendar, addWorkingDays(calendar, predecessor.earlyStart, lag), successor.earlyStart);
        case 'FF':
            return workingDaysBetween(calendar, addWorkingDays(calendar, predecessor.earlyFinish, lag), successor.earlyFinish);
        case 'SF':
            return workingDaysBetween(calendar, addWorkingDays(calendar, predecessor.earlyStart, lag), successor.earlyFinish);
    }
}
export function backwardPass(input) {
    const { forward, calendar } = input;
    const targetFinish = previousWorkingDay(calendar, input.targetFinish === undefined
        ? forward.projectFinish
        : requireIsoDate(input.targetFinish));
    const analyses = new Map();
    // Reverse topological order: every successor is analysed before the
    // predecessor that constrains it.
    for (let i = forward.order.length - 1; i >= 0; i--) {
        const taskId = forward.order[i];
        if (taskId === undefined)
            continue;
        const schedule = forward.schedules.get(taskId);
        if (!schedule)
            continue;
        let lateFinish = targetFinish;
        for (const dependency of forward.graph.successors.get(taskId) ?? []) {
            const successor = analyses.get(dependency.successorId);
            if (!successor)
                continue;
            lateFinish = minDate(lateFinish, latestFinishFrom(calendar, dependency, successor, schedule.durationDays));
        }
        const lateStart = startOfTask(calendar, lateFinish, schedule.durationDays);
        const totalFloat = workingDaysBetween(calendar, schedule.earlyFinish, lateFinish);
        analyses.set(taskId, {
            ...schedule,
            lateStart,
            lateFinish,
            totalFloat,
            // Filled in below, once every successor has an analysis.
            freeFloat: 0,
            critical: totalFloat <= 0,
        });
    }
    // Free float needs the successors' EARLY dates, which exist for every task
    // already, so a second sweep in any order will do.
    for (const [taskId, analysis] of analyses) {
        const outgoing = forward.graph.successors.get(taskId) ?? [];
        let freeFloat = analysis.totalFloat;
        for (const dependency of outgoing) {
            const successor = forward.schedules.get(dependency.successorId);
            if (!successor)
                continue;
            freeFloat = Math.min(freeFloat, slackAgainst(calendar, dependency, analysis, successor));
        }
        // A task without successors is bounded only by the project, so its free
        // float equals its total float. Free float is never negative: a schedule
        // cannot offer less than no room at all.
        analyses.set(taskId, {
            ...analysis,
            freeFloat: Math.max(0, Math.min(freeFloat, analysis.totalFloat)),
        });
    }
    const criticalTasks = forward.order.filter((taskId) => analyses.get(taskId)?.critical === true);
    const behindTarget = [...analyses.values()].some((analysis) => analysis.totalFloat < 0);
    return ok({
        tasks: analyses,
        projectStart: forward.schedules.size > 0 ? earliestStart(forward) : targetFinish,
        earliestFinish: forward.projectFinish,
        targetFinish,
        criticalTasks,
        behindTarget,
    });
}
function earliestStart(forward) {
    let earliest;
    for (const schedule of forward.schedules.values()) {
        earliest =
            earliest === undefined
                ? schedule.earlyStart
                : minDate(earliest, schedule.earlyStart);
    }
    return earliest ?? forward.projectFinish;
}
/**
 * Forward and backward pass in one call. This is what the API and the Gantt
 * view use; the two passes are exported separately mainly for testing.
 */
export function calculateSchedule(input) {
    const forward = forwardPass(input);
    if (!forward.ok)
        return err(forward.error);
    return backwardPass({
        forward: forward.value,
        calendar: input.calendar,
        ...(input.targetFinish === undefined
            ? {}
            : { targetFinish: input.targetFinish }),
    });
}
//# sourceMappingURL=backward-pass.js.map